// src/components/rcv/DocumentosTab.jsx (Refactorizado)
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Grid,
  TextField,
  Button,
  MenuItem,
  Box,
  Checkbox,
  Tooltip,
  Chip,
  IconButton,
  Alert
} from '@mui/material';
import {
  Search,
  FileDownload,
  Link as LinkIcon,
  ErrorOutline,
  KeyboardArrowDown
} from '@mui/icons-material';
import { createLogger } from '../../utils/logger';

const logger = createLogger('RCV');

// Componentes compartidos refactorizados
import { 
  CardContainer, 
  FilterBar, 
  EmptyState,
  LoadingState,
  DataTable,
  FormSelect
} from '../shared';

// Componentes específicos y servicios
import DocumentoButton from './DocumentoButton';
import { documentosService } from '../../services/documentosService';
import { formatearRut, formatearMonto, formatearFecha, getTipoDocLabel } from '@/utils/formatters';

const DocumentosTab = () => {
  const { empresaActiva } = useSelector(state => state.empresa);
  const [rutEmpresa, setRutEmpresa] = useState('');
  const [dvEmpresa, setDvEmpresa] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [periodos, setPeriodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [documentos, setDocumentos] = useState([]);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [isFiltrando, setIsFiltrando] = useState(false);
  const [documentosFiltrados, setDocumentosFiltrados] = useState([]);
  const [documentosSeleccionados, setDocumentosSeleccionados] = useState({});
  const [mostrarSoloNoAsociados, setMostrarSoloNoAsociados] = useState(false);
  const [tipoOperacion, setTipoOperacion] = useState('COMPRA');

  // Cargar documentos
  const cargarDocumentos = useCallback(async () => {
    if (!rutEmpresa || !periodo) {
      setError('Debe seleccionar un período');
      return;
    }

    setLoading(true);
    setError('');
    setDocumentos([]);
    setDocumentosFiltrados([]);
    setDocumentosSeleccionados({});

    try {
      const data = await documentosService.getDocumentosPeriodo(rutEmpresa, periodo, tipoOperacion);
      setDocumentos(data);
      setDocumentosFiltrados(data);
    } catch (err) {
      setError(err.message || 'Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  }, [rutEmpresa, periodo, tipoOperacion]);

  // Generar periodos
  useEffect(() => {
    // Importar la función de generación de períodos
    import('../../utils/periodos').then(({ generarPeriodos }) => {
      const opciones = generarPeriodos();
      setPeriodos(opciones);
      if (opciones.length > 0) {
        setPeriodo(opciones[0].value);
      }
    });
  }, []);

  // Establecer valores predeterminados de la empresa activa
  useEffect(() => {
    if (empresaActiva) {
      setRutEmpresa(empresaActiva.rut);
      setDvEmpresa(empresaActiva.dv);
    }
  }, [empresaActiva]);

  // Efecto para cargar documentos cuando cambia el periodo o tipo de operación
  useEffect(() => {
    if (rutEmpresa && periodo) {
      cargarDocumentos();
    }
  }, [rutEmpresa, periodo, tipoOperacion, cargarDocumentos]);

  // Filtrar documentos
  useEffect(() => {
    if (filtroTexto.trim() === '') {
      setDocumentosFiltrados(documentos);
      setIsFiltrando(false);
      return;
    }
    setIsFiltrando(true);
    const textoBusqueda = filtroTexto.toLowerCase();
    const esVenta = tipoOperacion === 'VENTA';
    const filtrados = documentos.filter(doc =>
      (esVenta ? doc.rut_cliente : doc.rut_proveedor)?.toString().includes(textoBusqueda) ||
      doc.razon_social?.toLowerCase().includes(textoBusqueda) ||
      doc.folio?.toString().includes(textoBusqueda) ||
      doc.tipo_doc?.toLowerCase().includes(textoBusqueda)
    );
    setDocumentosFiltrados(filtrados);
  }, [filtroTexto, documentos, tipoOperacion]);

  // Manejar selección de documentos
  const handleToggleSeleccion = (id) => {
    setDocumentosSeleccionados(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Seleccionar todos los documentos
  const handleSeleccionarTodos = (event) => {
    if (event.target.checked) {
      const seleccion = {};
      documentosFiltrados.forEach(doc => {
        seleccion[doc.id] = true;
      });
      setDocumentosSeleccionados(seleccion);
    } else {
      setDocumentosSeleccionados({});
    }
  };

  // Exportar documentos
  const exportarDocumentos = () => {
    // Implementación de exportación aquí
    logger.info('Exportando documentos seleccionados');
    alert("Funcionalidad de exportación no implementada");
  };

  // Asociar documentos seleccionados
  const asociarSeleccionados = () => {
    // Implementación de asociación masiva aquí
    logger.info('Asociando documentos seleccionados');
    alert("Funcionalidad de asociación masiva no implementada");
  };

  const esVenta = tipoOperacion === 'VENTA';

  // Configuración de columnas para la tabla
  const tableColumns = [
    {
      field: 'tipo_doc',
      headerName: 'Tipo',
      renderCell: (val) => <Chip label={getTipoDocLabel(val)} size="small" variant="outlined" />
    },
    {
      field: esVenta ? 'rut_cliente' : 'rut_proveedor',
      headerName: esVenta ? 'RUT Cliente' : 'RUT Proveedor',
      renderCell: (val, row) => formatearRut(val, esVenta ? row.dv_cliente : row.dv_proveedor)
    },
    { 
      field: 'razon_social', 
      headerName: 'Razón Social',
      renderCell: (val) => (
        <Tooltip title={val || ''}>
          <span style={{ 
            whiteSpace: 'nowrap', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            display: 'block',
            maxWidth: 200
          }}>
            {val}
          </span>
        </Tooltip>
      )
    },
    { field: 'folio', headerName: 'Folio' },
    { 
      field: 'fecha_docto', 
      headerName: 'Fecha',
      renderCell: (val) => formatearFecha(val)
    },
    { 
      field: 'monto_total', 
      headerName: 'Monto',
      align: 'right',
      renderCell: (val) => formatearMonto(val)
    },
    { 
      field: 'estado', 
      headerName: 'Estado',
      align: 'center',
      renderCell: () => <Chip label="Pendiente" color="default" variant="outlined" size="small" />
    }
  ];

  // Acciones para la tabla
  const tableActions = [
    {
      icon: <LinkIcon fontSize="small" />,
      tooltip: 'Asociar documento',
      onClick: () => {}
    },
    {
      icon: (row) => (
        <DocumentoButton
          soloIcono={true}
          rutEmisor={esVenta ? row.rut_cliente : row.rut_proveedor}
          tipoDoc={row.tipo_doc}
          folio={row.folio}
          movimientoId={row.id}
          rutEmpresa={rutEmpresa}
          onDocumentoCreado={() => cargarDocumentos()}
        />
      ),
      tooltip: 'Ver/Subir documento',
      onClick: () => {}, // La acción se maneja dentro del DocumentoButton
      customRender: true // Propiedad para indicar que se usa renderizado personalizado
    }
  ];

  // Limpiar filtros
  const handleLimpiarFiltros = () => {
    setFiltroTexto('');
  };

  return (
    <CardContainer
      title="Documentos RCV"
      subheader="Visualice y gestione los documentos del RCV"
    >
      {/* Filtros */}
      <FilterBar 
        onFilter={cargarDocumentos}
        onReset={handleLimpiarFiltros}
      >
        <Grid item xs={12} md={4}>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <TextField
                label="RUT Empresa"
                placeholder="Ej: 77123456"
                fullWidth
                value={rutEmpresa}
                disabled={true}
                size="small"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="DV"
                placeholder="K"
                fullWidth
                value={dvEmpresa}
                disabled={true}
                size="small"
                inputProps={{ maxLength: 1 }}
              />
            </Grid>
          </Grid>
        </Grid>
        
        <Grid item xs={12} md={2}>
          <FormSelect
            name="tipoOperacion"
            label="Tipo"
            value={tipoOperacion}
            onChange={(e) => setTipoOperacion(e.target.value)}
            options={[{ value: 'COMPRA', label: 'Compras' }, { value: 'VENTA', label: 'Ventas' }]}
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <FormSelect
            name="periodo"
            label="Período"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            options={periodos.map(p => ({ value: p.value, label: p.label }))}
            size="small"
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <TextField
            label="Buscar en documentos"
            placeholder="RUT, razón social, folio..."
            fullWidth
            value={filtroTexto}
            onChange={(e) => setFiltroTexto(e.target.value)}
            size="small"
          />
        </Grid>
      </FilterBar>

      {/* Estado de error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} icon={<ErrorOutline />}>
          {error}
        </Alert>
      )}
      
      {/* Tabla de documentos */}
      <LoadingState loading={loading} error={error}>
        {documentosFiltrados.length === 0 ? (
          <EmptyState
            usePaper={true}
            title="No hay documentos para mostrar"
            message={isFiltrando 
              ? "No se encontraron documentos con los filtros aplicados." 
              : "Seleccione un período y haga clic en Buscar para ver los documentos disponibles."
            }
            actionText={isFiltrando ? "Limpiar filtros" : null}
            onAction={isFiltrando ? handleLimpiarFiltros : null}
          />
        ) : (
          <>
            <DataTable
              columns={tableColumns}
              data={documentosFiltrados}
              actions={tableActions}
              selectable={true}
              onRowSelect={handleToggleSeleccion}
              pagination={true}
              stickyHeader={true}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Box>
                <Chip 
                  label={`Total: ${documentos.length} documentos`}
                  color="primary" 
                  variant="outlined"
                  size="small"
                />
                {isFiltrando && (
                  <Chip 
                    label={`Filtrados: ${documentosFiltrados.length}`}
                    color="secondary" 
                    variant="outlined"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={exportarDocumentos}
                  disabled={documentos.length === 0}
                >
                  Exportar
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={asociarSeleccionados}
                  disabled={Object.values(documentosSeleccionados).filter(Boolean).length === 0}
                >
                  Asociar seleccionados
                </Button>
              </Box>
            </Box>
          </>
        )}
      </LoadingState>
    </CardContainer>
  );
};

export default DocumentosTab;